import { useEffect, useRef } from "react";
import type { WsMessage, ActivityPayload } from "@wedding/shared";

type ActivityHandler = (payload: ActivityPayload) => void;

/**
 * Subscribes to the activity WebSocket (/activity/ws) and fires `onActivity`
 * for every incoming "activity" message. Handles reconnect automatically.
 *
 * Mount in Layout so all pages receive badge updates and toasts.
 * Toast rendering is suppressed on the slideshow route by the caller.
 */
export function useActivityWs(
  slug: string,
  guestId: string | undefined,
  onActivity: ActivityHandler,
  opts?: { enabled?: boolean }
) {
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const guestIdRef = useRef(guestId);
  guestIdRef.current = guestId;

  const slugRef = useRef(slug);
  slugRef.current = slug;

  const enabled = opts?.enabled ?? true;

  useEffect(() => {
    if (!slug || !enabled) return;

    let cancelled = false;
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let attempt = 0; // for exponential backoff

    function connect() {
      if (cancelled) return;

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(
        `${protocol}//${window.location.host}/api/w/${slugRef.current}/activity/ws`
      );

      ws.onopen = () => {
        attempt = 0; // healthy connection — reset backoff
        // Keepalive: ping every 45s so Cloudflare's edge doesn't close the idle
        // socket (the DO auto-replies "pong"). Stops the reconnect→KV-put storm.
        pingTimer = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) ws.send("ping");
        }, 45000);
      };

      ws.onmessage = (event) => {
        if (cancelled) return;
        if (event.data === "pong") return; // keepalive ack — ignore
        try {
          const msg = JSON.parse(event.data as string) as WsMessage;
          if (
            msg.type === "activity" &&
            msg.activity &&
            msg.activity.actorGuestId !== guestIdRef.current
          ) {
            onActivityRef.current(msg.activity);
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onclose = () => {
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        if (cancelled) return;
        // Exponential backoff + jitter so a failing endpoint (e.g. server 500s)
        // isn't hammered every few seconds.
        const delay = Math.min(60000, 3000 * 2 ** attempt) * (0.8 + Math.random() * 0.4);
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws?.close();
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      ws?.close();
    };
  // Intentionally only depends on slug/enabled — guestId reads from ref to avoid
  // tearing down and rebuilding the WebSocket connection on every re-render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, enabled]);
}
